import React, { useEffect, useState } from 'react';
import Modal from '@app/react-common/components/modal/Modal';
import Button from '@app/react-common/components/button/Button';
import Input from '@app/react-common/components/input/Input';
import RadioButtonGroup, { RadioButtonOption } from '@app/react-common/components/radio-button-group/RadioButtonGroup';
import {
  ExtensionsGroupType,
  ExtensionsGroupTypeToDisplayName,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';

export enum ExtensionGroupModalMode {
  CREATE = 'create',
  RENAME = 'rename',
  DELETE = 'delete',
}

enum CreateExtensionGroupStep {
  CHOOSE_GROUP_TYPE = 'chooseGroupType',
  NAME_AND_DESCRIPTION = 'nameAndDescription',
}

const GROUP_TYPE_OPTIONS: RadioButtonOption[] = [
  {
    label: ExtensionsGroupTypeToDisplayName[ExtensionsGroupType.UPSELL],
    value: ExtensionsGroupType.UPSELL,
    description: 'Product upsells shown during checkout',
  },
  {
    label: ExtensionsGroupTypeToDisplayName[ExtensionsGroupType.EXTENSIONS],
    value: ExtensionsGroupType.EXTENSIONS,
    description: 'All other extension types',
  },
];

interface ExtensionGroupModalProps {
  mode: ExtensionGroupModalMode;
  isOpen: boolean;
  onClose: () => void;
  onCreateSuccess?: (data: ExtensionGroupFormData) => Promise<void>;
  onRenameSuccess?: (data: ExtensionGroupFormData) => Promise<void>;
  onDeleteSuccess?: () => Promise<void>;
  groupName?: string;
  groupDescription?: string;
}

export interface ExtensionGroupFormData {
  name: string;
  description: string;
  groupType: ExtensionsGroupType;
}

export const ExtensionGroupModal: React.FC<ExtensionGroupModalProps> = (props) => {
  const { getTitle, getButton } = useNamingUtils();
  const [showValidation, setShowValidation] = useState(false);
  const [createStep, setCreateStep] = useState(CreateExtensionGroupStep.CHOOSE_GROUP_TYPE);

  const [formData, setFormData] = useState<ExtensionGroupFormData>({
    name: props?.groupName || '',
    description: props?.groupDescription || '',
    groupType: ExtensionsGroupType.UPSELL,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      name: props.groupName || '',
      description: props.groupDescription || '',
    }));
  }, [props.groupName, props.groupDescription]);

  useEffect(() => {
    if (props.isOpen) {
      setCreateStep(CreateExtensionGroupStep.CHOOSE_GROUP_TYPE);
      setShowValidation(false);
    }
  }, [props.isOpen]);

  const isCreating = props.mode === ExtensionGroupModalMode.CREATE;
  const isChoosingGroupType = isCreating && createStep === CreateExtensionGroupStep.CHOOSE_GROUP_TYPE;
  const isNamingNewGroup = isCreating && createStep === CreateExtensionGroupStep.NAME_AND_DESCRIPTION;
  const showNameAndDescription = props.mode === ExtensionGroupModalMode.RENAME || isNamingNewGroup;

  const handleButtonClick = async () => {
    if (isChoosingGroupType) {
      setCreateStep(CreateExtensionGroupStep.NAME_AND_DESCRIPTION);
    } else if (isCreating) {
      await props.onCreateSuccess?.(formData);
    } else if (props.mode === ExtensionGroupModalMode.RENAME) {
      await props.onRenameSuccess?.(formData);
    } else if (props.mode === ExtensionGroupModalMode.DELETE) {
      await props.onDeleteSuccess?.();
    }
  };

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} defaultPadding={false}>
      <div className="extension-group-modal">
        <div className="extension-group-modal__header">
          <h2 className="extension-group-modal__title">
            {getTitle(props.mode, createStep, formData.groupType, props.groupName)}
          </h2>
          <Button
            onClick={props.onClose}
            icon="close"
            ariaLabel="Close"
            size="medium"
            variant="ghost-light"
            className="extension-group-modal__header__close"
          />
        </div>

        <div className="extension-group-modal__body">
          {isChoosingGroupType && (
            <div className="extension-group-modal__group-type">
              <span className="extension-group-modal__group-type__label">What type of group is this?</span>
              <RadioButtonGroup
                options={GROUP_TYPE_OPTIONS}
                defaultOptionValue={formData.groupType}
                disabled={false}
                onChange={(option: RadioButtonOption) =>
                  setFormData((prevData) => ({ ...prevData, groupType: option.value as ExtensionsGroupType }))
                }
              />
            </div>
          )}

          {showNameAndDescription && (
            <>
              <Input
                wrapperDivClassName="extension-group-modal__input"
                required={true}
                showValidation={showValidation}
                type="text"
                placeholder="New experience"
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
              />
              <Input
                wrapperDivClassName="extension-group-modal__input"
                type="text"
                placeholder="My store"
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
              />
            </>
          )}
        </div>

        <div className="extension-group-modal__actions">
          <Button
            onClick={() =>
              isNamingNewGroup ? setCreateStep(CreateExtensionGroupStep.CHOOSE_GROUP_TYPE) : props.onClose()
            }
            text={isNamingNewGroup ? 'Back' : 'Cancel'}
            size="medium"
            variant="secondary"
            className="extension-group-modal__actions__cancel"
          />
          <Button
            onClick={async () => {
              if (showNameAndDescription && !(formData?.name?.length > 0)) {
                setShowValidation(true);
                return;
              }
              await handleButtonClick();
            }}
            text={getButton(props.mode, createStep)}
            size="medium"
            variant={props.mode === ExtensionGroupModalMode.DELETE ? 'danger' : 'primary'}
          />
        </div>
      </div>
    </Modal>
  );
};

function useNamingUtils() {
  const getTitle = (
    mode: string,
    createStep: CreateExtensionGroupStep,
    groupType: ExtensionsGroupType,
    groupName?: string,
  ): string => {
    if (mode === ExtensionGroupModalMode.CREATE) {
      return createStep === CreateExtensionGroupStep.CHOOSE_GROUP_TYPE
        ? 'New extension group'
        : `New ${ExtensionsGroupTypeToDisplayName[groupType].toLowerCase()}`;
    } else if (mode === ExtensionGroupModalMode.RENAME) {
      return 'Rename Extension Group';
    } else if (mode === ExtensionGroupModalMode.DELETE) {
      return `Are you sure you want to delete "${groupName}"?`;
    }
    return '';
  };

  const getButton = (mode: string, createStep: CreateExtensionGroupStep): string => {
    if (mode === ExtensionGroupModalMode.CREATE) {
      return createStep === CreateExtensionGroupStep.CHOOSE_GROUP_TYPE ? 'Next' : 'Create';
    } else if (mode === ExtensionGroupModalMode.RENAME) {
      return 'Save';
    } else if (mode === ExtensionGroupModalMode.DELETE) {
      return 'Delete';
    }
    return '';
  };

  return { getTitle, getButton };
}
